import type { AriaProps, AccessibleFormProps } from '../types/accessibility.types';
import { a11y } from '../config/library.config';

/**
 * Valida e genera props ARIA automatiche
 */
export function getAriaProps(props: Partial<AriaProps>, autoLabel?: string): AriaProps {
  const ariaProps: AriaProps = { ...props };

  // Genera aria-label automatico se abilitato nella config e mancante
  if (a11y.ariaDefaults.autoAriaLabel && !ariaProps['aria-label'] && !ariaProps['aria-labelledby'] && autoLabel) {
    ariaProps['aria-label'] = autoLabel;
  }

  return ariaProps;
}

/**
 * Genera un ID univoco per elementi di form
 */
let idCounter = 0;
export function generateId(prefix: string = 'lib'): string {
  idCounter++;
  return `${prefix}-${Date.now()}-${idCounter}`;
}

/**
 * Valida e prepara props per elementi di form accessibili
 */
export function getAccessibleFormProps(props: AccessibleFormProps): {
  inputProps: Record<string, any>;
  labelProps: Record<string, any>;
  errorProps: Record<string, any>;
  helpProps: Record<string, any>;
  ids: {
    input: string;
    error: string;
    help: string;
  };
} {
  const inputId = props.id || generateId('input');
  const errorId = `${inputId}-error`;
  const helpId = `${inputId}-help`;

  const inputProps: Record<string, any> = {
    id: inputId,
    name: props.name || inputId,
    'aria-invalid': props.error || props['aria-invalid'],
    'aria-required': props.required || props['aria-required'],
  };

  // Aggiungi aria-describedby per errori e help text
  const describedBy: string[] = [];
  if (props.error && props.errorMessage) {
    describedBy.push(errorId);
  }
  if (props.helpText) {
    describedBy.push(helpId);
  }
  if (props['aria-describedby']) {
    describedBy.push(props['aria-describedby']);
  }
  if (describedBy.length > 0) {
    inputProps['aria-describedby'] = describedBy.join(' ');
  }

  // Altre props ARIA
  if (props['aria-label']) inputProps['aria-label'] = props['aria-label'];
  if (props['aria-labelledby']) inputProps['aria-labelledby'] = props['aria-labelledby'];
  if (props.role) inputProps.role = props.role;

  return {
    inputProps,
    labelProps: {
      htmlFor: inputId,
    },
    errorProps: {
      id: errorId,
      role: 'alert',
      'aria-live': 'polite' as const,
    },
    helpProps: {
      id: helpId,
    },
    ids: {
      input: inputId,
      error: errorId,
      help: helpId,
    },
  };
}

/**
 * Valida il contrast ratio tra due colori (hex)
 * Semplificata - per uso production considerare una libreria dedicata
 */
export function validateContrastRatio(foreground: string, background: string): boolean {
  // Implementazione semplificata - in produzione usa una libreria come 'wcag-contrast'
  // Per ora ritorniamo sempre true, ma la struttura è pronta per validazione reale
  return true;
}

/**
 * Ottieni le classi per focus styles accessibili
 */
export function getFocusClasses(): string {
  return 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current';
}

/**
 * Verifica se un elemento è disabilitato e ritorna le props corrette
 */
export function getDisabledProps(disabled?: boolean): {
  disabled?: boolean;
  'aria-disabled'?: boolean;
  tabIndex?: number;
} {
  if (!disabled) return {};

  return {
    disabled: true,
    'aria-disabled': true,
    tabIndex: -1,
  };
}

